/**
 * @fileoverview Connect the administration workspace to versioned ecosystem documentation served by the backend. The manifest owns page identity and review metadata; selected Markdown is read content. URL selection supports deep links and internal documentation navigation. This surface explains operations rather than executing them; source-oriented code documentation is separately built by `scripts/docstrings.py`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'

import api from '../../api'
import { AdminCard, AdminEmptyState, AdminNote, AdminPage } from './AdminChrome'
import './Docs.css'

export const DOC_QUERY_PARAM = 'doc'

export const FALLBACK_PAGES = [
    { id: 'overview', title: 'Overview', file: 'overview.md', summary: 'Ecosystem map, DNS, and page index' },
    { id: 'internal-architecture', title: 'Internal architecture', file: 'internal-architecture.md', summary: 'Prod internal app, Traefik, workers' },
    { id: 'servers', title: 'Servers', file: 'servers.md', summary: 'DigitalOcean droplets, VPCs, DNS' },
    { id: 'databases-and-storage', title: 'Databases and storage', file: 'databases-and-storage.md', summary: 'SQL, MongoDB, OpenSearch, object storage' },
    { id: 'n8n', title: 'n8n', file: 'n8n.md', summary: 'Prod n8n / n8n1 and staging n8n' },
    { id: 'staging', title: 'Staging', file: 'staging.md', summary: 'Staging internal, Caddy, local data plane' },
    { id: 'phone', title: 'Phone', file: 'phone.md', summary: 'FreeSWITCH phone server and in-app phone settings' },
    { id: 'images-and-repos', title: 'Images and repos', file: 'images-and-repos.md', summary: 'GHCR images, GitHub org, CI tags' },
    { id: 'other-services', title: 'Other services', file: 'other-services.md', summary: 'Slack viewer, archiver, public site, local ports' },
    { id: 'external-integrations', title: 'External integrations', file: 'external-integrations.md', summary: 'Major SaaS and API boundaries' },
    { id: 'maintenance', title: 'Maintenance', file: 'maintenance.md', summary: 'Weekly audit checklist and security rules' },
]



function pageIdFromHref(href) {
    if (!href || href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
        return null
    }
    const withoutQuery = href.split('#')[0].split('?')[0]
    const filename = withoutQuery.replace(/^\.\//, '').split('/').pop()
    if (!filename || !filename.endsWith('.md')) {
        return null
    }
    return filename.replace(/\.md$/, '')
}



function splitRow(line) {
    return line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim())
}



function isSeparatorLine(line) {
    return /^\s*\|?\s*:?-{3,}[-|:\s]*$/.test(line)
}



function parseTable(block) {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean)
    if (lines.length < 2 || !isSeparatorLine(lines[1])) {
        return null
    }
    return {
        header: splitRow(lines[0]),
        rows: lines.slice(2).map(splitRow),
    }
}



function splitMarkdownChunks(markdown) {
    const lines = String(markdown || '').split('\n')
    const chunks = []
    let buffer = []
    const flushMarkdown =

    () => {
        if (!buffer.length) return
        chunks.push({ type: 'md', text: buffer.join('\n') })
        buffer = []
    }

    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index]
        const next = lines[index + 1] || ''
        if (line.includes('|') && isSeparatorLine(next)) {
            flushMarkdown()
            const tableLines = [line, next]
            index += 2
            while (index < lines.length && lines[index].includes('|')) {
                tableLines.push(lines[index])
                index += 1
            }
            index -= 1
            chunks.push({ type: 'table', text: tableLines.join('\n') })
        } else {
            buffer.push(line)
        }
    }
    flushMarkdown()
    return chunks
}



function MarkdownBody({ markdown, components }) {
    const chunks = useMemo(() => splitMarkdownChunks(markdown), [markdown])
    return (
        <div className="admin-docs-markdown">
            {chunks.map((chunk, index) => {
                if (chunk.type === 'table') {
                    const table = parseTable(chunk.text)
                    if (!table) {
                        return <ReactMarkdown key={index} components={components}>{chunk.text}</ReactMarkdown>
                    }
                    return (
                        <table key={index}>
                            <thead>
                                <tr>
                                    {table.header.map((cell, cellIndex) => (
                                        <th key={cellIndex}>
                                            <ReactMarkdown components={components}>{cell}</ReactMarkdown>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex}>
                                                <ReactMarkdown components={components}>{cell}</ReactMarkdown>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
                return <ReactMarkdown key={index} components={components}>{chunk.text}</ReactMarkdown>
            })}
        </div>
    )
}



function Docs({ embedded = false }) {
    const [searchParams, setSearchParams] = useSearchParams()
    const [pages, setPages] = useState(FALLBACK_PAGES)
    const [lastReviewed, setLastReviewed] = useState(null)
    const [markdown, setMarkdown] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const requestedId = searchParams.get(DOC_QUERY_PARAM)
    const activePage = useMemo(() => {
        if (requestedId && pages.some((page) => page.id === requestedId)) {
            return pages.find((page) => page.id === requestedId)
        }
        return pages[0]
    }, [pages, requestedId])

    const selectPage = useCallback(

    (pageId) => {
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set(DOC_QUERY_PARAM, pageId)
        if (nextSearchParams.toString() === searchParams.toString()) {
            return
        }
        setSearchParams(nextSearchParams, { replace: true })
    }, [searchParams, setSearchParams])

    useEffect(

    () => {
        let cancelled = false


        async function loadManifest() {
            try {
                const response = await api.get('/admin/docs/manifest')
                const payload = response.data
                if (cancelled) return
                if (Array.isArray(payload.pages) && payload.pages.length) {
                    setPages(payload.pages)
                }
                if (payload.lastReviewed) {
                    setLastReviewed(payload.lastReviewed)
                }
            } catch (manifestError) {
                if (!cancelled) {
                    setLastReviewed(null)
                }
            }
        }
        loadManifest()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(

    () => {
        if (!activePage?.file) {
            return undefined
        }
        let cancelled = false


        async function loadPage() {
            setLoading(true)
            setError(null)
            try {
                const response = await api.get(`/admin/docs/${encodeURIComponent(activePage.id)}`)
                if (cancelled) return
                setMarkdown(response.data?.markdown || '')
            } catch (loadError) {
                if (cancelled) return
                setMarkdown('')
                setError(loadError.response?.data?.error || 'Unable to load documentation')
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }
        loadPage()
        return () => {
            cancelled = true
        }
    }, [activePage])

    const markdownComponents = useMemo(() => ({


        a({ href, children, ...props }) {
            const docId = pageIdFromHref(href)
            if (docId) {
                return (
                    <a
                        href={`?tab=docs&${DOC_QUERY_PARAM}=${docId}`}
                        onClick={(event) => {
                            event.preventDefault()
                            selectPage(docId)
                        }}
                    >
                        {children}
                    </a>
                )
            }
            const isExternal = href && /^https?:\/\//i.test(href)
            return (
                <a
                    href={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    {...props}
                >
                    {children}
                </a>
            )
        },
    }), [selectPage])

    const body = (
        <div className="admin-docs">
            <nav className="admin-docs__nav" aria-label="Ecosystem documentation">
                {pages.map((page) => {
                    const selected = page.id === activePage?.id
                    return (
                        <button
                            key={page.id}
                            type="button"
                            className={`admin-docs__nav-item${selected ? ' is-selected' : ''}`}
                            aria-current={selected ? 'page' : undefined}
                            onClick={() => selectPage(page.id)}
                        >
                            <span className="admin-docs__nav-title">{page.title}</span>
                            {page.summary ? <span className="admin-docs__nav-summary">{page.summary}</span> : null}
                        </button>
                    )
                })}
            </nav>
            <AdminCard
                title={activePage?.title || 'Docs'}
                description={activePage?.id === 'overview' && lastReviewed
                    ? `Last reviewed ${lastReviewed}. Weekly review intended.`
                    : activePage?.summary}
            >
                {error ? (
                    <AdminNote tone="warning" title="Documentation failed to load">
                        {error}. Confirm the backend docs sync ran before this start or image build.
                    </AdminNote>
                ) : null}
                {loading ? (
                    <div className="admin-docs__status">Loading…</div>
                ) : null}
                {!loading && !error && !markdown ? (
                    <AdminEmptyState title="No content" description="This page is empty." />
                ) : null}
                {!loading && !error && markdown ? (
                    <MarkdownBody markdown={markdown} components={markdownComponents} />
                ) : null}
            </AdminCard>
        </div>
    )

    if (embedded) {
        return body
    }

    return (
        <AdminPage
            title="Ecosystem docs"
            description="Internal ops reference for servers, data stores, n8n, staging, and related services."
        >
            {body}
        </AdminPage>
    )
}

export default Docs
